import { Box, Stack, Typography } from '@mui/material'

import { palette } from 'constants/theme'
import { StatusChip } from '../StatusChip'

// One entry, fed to the shared StatusChip. StatusChip derives fill/border/text from a
// single hue and handles light vs dark itself, so the pill matches every other status
// badge in the app instead of being a second, slightly-different pill. The hand-rolled
// version this replaces was too wide, under-padded, and used its own opacity ramp
// (working-conventions §28: reuse the shared component rather than rebuild it).
const COMING_SOON_STATUS = {
  comingSoon: { label: 'Coming soon', color: palette.green[500] },
}

/**
 * Tooltip content for a capability we advertise but cannot yet fulfil: a green
 * "Coming soon" pill above the explanation.
 *
 * Shared rather than inlined because the same note appears on two surfaces — the shop
 * card's capability badge and the product page's capability chip — and they must say
 * the same thing. Two hand-rolled copies is exactly how the badge/chip colours drifted
 * before (§36: extract on the second hand-roll).
 *
 * Green, not the amber/grey you might expect for "unavailable": the pill is announcing
 * something positive and forthcoming, not warning about a failure. The muting that says
 * "you can't buy this yet" is already carried by the greyed badge the tooltip hangs off,
 * so the tooltip's job is the good news plus the way to act on it.
 *
 * Colour handling belongs to StatusChip, which derives fill/border/text from the one
 * hue and resolves light vs dark itself — so the pill reads on the tooltip surface in
 * both modes (white in light, near-black in dark) without this file knowing how.
 */
export const ComingSoonNote = ({ children }: { children: string }) => {
  return (
    <Stack spacing={2} sx={{ maxWidth: 260 }}>
      <Box sx={{ alignSelf: 'flex-start' }}>
        <StatusChip statuses={COMING_SOON_STATUS} status="comingSoon" size="small" />
      </Box>
      {/* 1.5 so the sentence doesn't read dense in a narrow tooltip (§15). */}
      <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Stack>
  )
}
